// src/components/forms/order/OrderActions.tsx
// Componente para botones flotantes y acciones del pedido

import React from 'react'
import { ReceiptRefundIcon, TruckIcon, DocumentTextIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline'
import FalabellaTrackingModal from '../../modals/FalabellaTrackingModal'
import FalabellaShippingLabelModal from '../../modals/FalabellaShippingLabelModal'
import FalabellaInvoiceUploadModal from '../../modals/FalabellaInvoiceUploadModal'
import AuditHistory from '../../AuditHistory'
import { NotesModal } from '../shared'
import { Button } from '../../ui'
import type { OrderData } from '../../../types/orderFormTypes'

interface OrderActionsProps {
  formData: OrderData
  mode: 'view' | 'edit' | 'create'
  fieldsLocked?: boolean
  showNoteModal: boolean
  setShowNoteModal: (show: boolean) => void
  showAuditModal: boolean
  setShowAuditModal: (show: boolean) => void
  showTrackingModal: boolean
  setShowTrackingModal: (show: boolean) => void
  showShippingLabelModal: boolean
  setShowShippingLabelModal: (show: boolean) => void
  showInvoiceUploadModal: boolean
  setShowInvoiceUploadModal: (show: boolean) => void
  onNoteChange: (value: string) => void
}

export const OrderActions: React.FC<OrderActionsProps> = ({
  formData,
  mode,
  fieldsLocked = false,
  showNoteModal,
  setShowNoteModal,
  showAuditModal,
  setShowAuditModal,
  showTrackingModal,
  setShowTrackingModal,
  showShippingLabelModal,
  setShowShippingLabelModal,
  showInvoiceUploadModal,
  setShowInvoiceUploadModal,
  onNoteChange
}) => {
  const isFalabella = formData.store_name === 'Falabella'
  const note = formData.extraDetails?.customer_note || ''

  return (
    <>
      {/* Botón flotante de notas */}
      <NotesModal
        showModal={showNoteModal}
        setShowModal={setShowNoteModal}
        note={note}
        onNoteChange={onNoteChange}
        mode={fieldsLocked ? 'view' : mode}
        title="Notas"
        placeholder="Ingrese las notas del pedido..."
        position="right-4"
        buttonStyle="rounded-none"
      />

      {/* Botones flotantes para modo edit y view */}
      {(mode === 'edit' || mode === 'view') && (
        <>
          {/* Botón de historial de auditoría */}
          <div className="fixed right-4 top-36 z-50">
            <div className="relative flex items-center gap-2">
              <Button
                type="button"
                onClick={() => setShowAuditModal(true)}
                variant="success"
                className="rounded-none p-3 shadow-lg max-w-11"
                title="Ver historial de cambios"
              >
                <ReceiptRefundIcon className="h-5 w-5" />
              </Button>
            </div>
          </div>
          
          {/* Botones de Falabella */}
          {isFalabella && (
            <>
              {/* Botón de seguimiento Falabella */}
              <div className="fixed right-4 top-48 z-50">
                <div className="relative flex items-center gap-2">
                  <Button
                    type="button"
                    onClick={() => setShowTrackingModal(true)}
                    variant="warning"
                    className="rounded-none p-3 shadow-lg bg-orange-600 hover:bg-orange-700 max-w-11"
                    title="Ver N° Seguimiento"
                  >
                    <TruckIcon className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Botones de Falabella (etiqueta y documento) */}
              <div className="fixed right-4 top-60 z-50">
                <div className="relative flex flex-col gap-2">
                  <Button
                    type="button"
                    onClick={() => setShowShippingLabelModal(true)}
                    variant="primary"
                    className="rounded-none p-3 shadow-lg bg-purple-600 hover:bg-purple-700 max-w-11"
                    title="Generar Etiqueta de Envío"
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowInvoiceUploadModal(true)}
                    variant="primary"
                    className="rounded-none p-3 shadow-lg bg-indigo-600 hover:bg-indigo-700 max-w-11"
                    title="Subir Documento Tributario"
                  >
                    <CloudArrowUpIcon className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Modales */}
      {isFalabella && (
        <>
          <FalabellaTrackingModal
            isOpen={showTrackingModal}
            onClose={() => setShowTrackingModal(false)}
            ordersId={formData.orders_id?.toString() || ''}
            orderNumber={formData.orders_id?.toString()}
          />

          <FalabellaShippingLabelModal
            isOpen={showShippingLabelModal}
            onClose={() => setShowShippingLabelModal(false)}
            ordersId={formData.orders_id?.toString() || ''}
            orderNumber={formData.orders_id?.toString()}
          />

          <FalabellaInvoiceUploadModal
            isOpen={showInvoiceUploadModal}
            onClose={() => setShowInvoiceUploadModal(false)}
            ordersId={formData.orders_id?.toString() || ''}
            orderNumber={formData.orders_id?.toString()}
          />
        </>
      )}

      {/* Modal de historial de auditoría */}
      <AuditHistory
        orderId={formData.id as number}
        isVisible={showAuditModal}
        onClose={() => setShowAuditModal(false)}
      />
    </>
  )
}

